import { CommonStudent } from '@codidae/common-types';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import AltusTalk from 'components/altus-talk/altus-talk';
import AnsweredQuestionsChart from 'components/answered-questions-chart/answered-questions-chart';
import Card from 'components/card/card';
import Flex from 'components/flex/flex';
import InfoTooltip from 'components/info-tooltip/info-tooltip';
import ParentStatsScreentime from 'components/parent-stats-screentime/parent-stats-screentime';
import StatsNumber from 'components/stats-number/stats-number';
import StatsSessionResult from 'components/stats-session-result/stats-session-result';
import { useMemo } from 'react';
import { getChildrenSessionResults } from 'services/childrens';
import { getScreentimeForPeriod } from 'services/screentime';
import { defaultTimePeriod, formatTime } from 'utils/date';
import styles from './children-stats.module.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

/* eslint-disable-next-line */
export interface ChildrenStatsProps {
  children: CommonStudent;
}

export function ChildrenStats({ children: child }: ChildrenStatsProps) {
  const { id } = child;
  const [{ data: results }] = useQueries({
    queries: [
      {
        queryKey: ['results', id],
        queryFn: () => getChildrenSessionResults(id),
        staleTime: 0,
      },
    ],
  });
  const { data: screentimes } = useQuery({
    queryKey: ['getScreentimeForPeriod', child.id],
    queryFn: () => getScreentimeForPeriod(Number(child.id), new Date(), new Date()),
  });

  const todaySessions = results?.filter(
    (result) =>
      new Date(result.createdAt).getDate() === new Date().getDate() &&
      new Date(result.createdAt).getMonth() === new Date().getMonth() &&
      new Date(result.createdAt).getFullYear() === new Date().getFullYear(),
  );

  const dailyUsages = useMemo(() => {
    if (!screentimes) return [];
    const dus = [] as {
      createdAt: Date;
      usage: number;
    }[];
    screentimes.forEach((screentime) => {
      screentime.screentimes.forEach((st) => {
        const date = new Date(st.createdAt);
        const existing = dus.find((daily) => daily.createdAt.toDateString() === date.toDateString());
        if (existing) {
          existing.usage += Math.round(st.duration / 60);
        } else {
          dus.push({
            createdAt: date,
            usage: Math.round(st.duration / 60),
          });
        }
      });
    });
    dus.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    return dus;
  }, [screentimes]);

  const { periodUsage } = useMemo(() => {
    const computedPeriodUsage = dailyUsages
      ?.filter((use) => new Date().toDateString() === use.createdAt.toDateString())
      .reduce((acc, use) => acc + use.usage, 0);
    return { periodUsage: computedPeriodUsage };
  }, [dailyUsages]);

  return (
    <Flex direction="column" justify="start" align="center" width="100%" height="100%">
      <Flex
        direction="column"
        gap={10}
        pad={20}
        width="100%"
        height="100%"
        style={{
          overflowY: 'scroll',
          paddingBottom: 50,
          paddingTop: 20,
        }}
      >
        <Flex gap={10} width="100%" style={{ marginBottom: 10 }}>
          <StatsNumber
            number={todaySessions?.length ?? 0}
            text="quiz aujourd'hui"
            tooltipInfo={<div className={styles.textToolTip}>Nombre de quiz effectué aujourd&apos;hui.</div>}
          />
          <StatsNumber
            number={formatTime(periodUsage)}
            text="de temps d'écran aujourd'hui"
            tooltipInfo={<div className={styles.textToolTip}>Temps de divertissement aujourd&apos;hui.</div>}
          />
        </Flex>

        <ParentStatsScreentime childrenId={child.id.toString()} />

        <div style={{ height: 40 }} />

        {!!results?.filter((q) => q.answeredQuestions.length > 0).length && (
          <Card style={{ padding: 15, width: '100%' }}>
            <AnsweredQuestionsChart results={results} timeRangeName="cette semaine" timePeriod={defaultTimePeriod} />
          </Card>
        )}
        <div style={{ height: 40 }} />
        {!!results?.filter((q) => q.answeredQuestions.length > 0).length && (
          <Card style={{ padding: 15, width: '100%' }}>
            <div className={styles.inputWrapper}>
              <span className={styles.label}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Mes derniers quiz</div>
                <InfoTooltip>
                  <div className={styles.textToolTip}>Nombre de bonnes réponses sur le nombre total de questions.</div>
                </InfoTooltip>
              </span>
            </div>
            {results
              ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .filter((q) => q.answeredQuestions.length > 0)
              .slice(0, 7)
              .map((result) => <StatsSessionResult key={result.id} result={result} />)}
          </Card>
        )}
        {!results?.filter((q) => q.answeredQuestions.length > 0).length && (
          <AltusTalk variant="small" type="hard">
            Je n&apos;ai pas encore reçu de donnée pour afficher des statistiques sur les quiz ! Réalise un premier quiz
            pour commencer
          </AltusTalk>
        )}
      </Flex>
      <div style={{ height: 70 }} />
    </Flex>
  );
}

export default ChildrenStats;
